import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  Button,
  Breadcrumb,
  Tag,
  Space,
  Modal,
  Card,
  Row,
  Col,
  Input,
  Statistic,
  Spin,
  Timeline,
  Descriptions,
} from 'antd';
import {
  HomeOutlined,
  EyeOutlined,
  SearchOutlined,
  InboxOutlined,
  UserOutlined,
  ToolOutlined,
  CheckCircleOutlined,
  FileExcelOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiRequest } from '../../../../service/apiRequest';
import { showError } from '../../../../components/errorHandler';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

interface PlyMdfItem {
  key: string;
  product_id: number;
  product_name: string;
  product_code: string;
  category_name: string;
  brand_name: string;
  unit_name: string;
  total_purchased_qty: number;
  total_issued_qty: number;
  available_qty: number;
  avg_rate: number;
  total_amount: number;
  purchase_bills: any[];
  issued_records: any[];
  work_orders: any[];
}

export const isPlyAndMdfCategory = (categoryName?: string | null): boolean => {
  if (!categoryName) return false;
  const cat = categoryName.trim().toLowerCase();

  // Exact or common slug matches
  if (
    cat === 'ply and mdf' ||
    cat === 'ply-and-mdf' ||
    cat === 'ply_and_mdf' ||
    cat === 'ply & mdf' ||
    cat === 'ply/mdf' ||
    cat === 'ply mdf' ||
    cat === 'ply' ||
    cat === 'mdf' ||
    cat === 'plywood'
  ) {
    return true;
  }

  // Token / word check on normalized category name (e.g. 'Ply & MDF Board', 'MDF Panels')
  const clean = cat.replace(/[-_&/\\+]/g, ' ');
  const words = clean.split(/\s+/).filter(Boolean);

  const hasPly = words.some((w) => w === 'ply' || w.startsWith('ply'));
  const hasMdf = words.some((w) => w === 'mdf' || w.startsWith('mdf'));

  return hasPly || hasMdf;
};

const PlyAndMdfInventory: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [products, setProducts] = useState<any[]>([]);
  const [poBills, setPoBills] = useState<any[]>([]);
  const [issues, setIssues] = useState<any[]>([]);
  const [workOrders, setWorkOrders] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // View Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<PlyMdfItem | null>(null);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [prodRes, poRes, issueRes, woRes] = await Promise.all([
        apiRequest<any>({ method: 'get', url: '/get-handicraft-products?limit=1000' }).catch(() => ({ data: [] })),
        apiRequest<any[]>({ method: 'get', url: '/get-po-bill' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/issue-master' }).catch(() => []),
        apiRequest<any[]>({ method: 'get', url: '/get-work-orders' }).catch(() => []),
      ]);

      const prodList = prodRes?.data || (Array.isArray(prodRes) ? prodRes : []);
      setProducts(prodList);
      setPoBills(poRes || []);
      setIssues(issueRes || []);
      setWorkOrders(woRes || []);
    } catch (err) {
      console.error(err);
      showError('Failed to load Ply & MDF inventory data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Filter and process Ply and MDF items (strictly where category is Ply and MDF)
  const inventoryData = useMemo(() => {
    const targetProducts = products.filter((p) =>
      isPlyAndMdfCategory(p.category_name)
    );

    return targetProducts.map((p) => {
      const prodId = Number(p.product_id);
      const prodName = p.product_name || 'Ply / MDF Sheet';
      const prodCode = p.product_code || '-';
      const categoryName = p.category_name || 'Ply & MDF';
      const brandName = p.brand_name || 'Standard';
      const unitName = p.ut_name || 'Sheets';

      // 1. Gather Purchase Bills
      const matchedBills: any[] = [];
      let totalPurchased = 0;
      let totalPurchaseCost = 0;

      poBills.forEach((bill: any) => {
        const details = bill.po_details || [];
        details.forEach((item: any) => {
          const itemProdId = Number(item.product_id || item.pb_product_id);
          const itemName = item.product_name || item.item_name;
          if (itemProdId === prodId || (itemName && itemName.toLowerCase() === prodName.toLowerCase())) {
            const qty = Number(item.pbg_qty || item.qty || item.quantity || 0);
            const rate = Number(item.pbg_rate || item.rate || item.price || item.pbg_price || 0);
            totalPurchased += qty;
            totalPurchaseCost += qty * rate;
            matchedBills.push({
              bill_id: bill.pbill_id,
              bill_no: bill.pbill_bill_no || `BILL-${bill.pbill_id}`,
              po_no: bill.pbill_po_no || '-',
              date: bill.pbill_date || bill.pbill_created_on,
              qty: qty,
              rate: rate,
              amount: qty * rate,
            });
          }
        });
      });

      // 2. Gather Store / Member Issues
      const matchedIssues: any[] = [];
      let totalIssued = 0;

      issues.forEach((issue: any) => {
        const itemsList = issue.items || [];
        const issueTo = issue.employee_name || issue.im_issue_to || 'Member / Worker';
        const issueDate = issue.im_created_on || issue.created_at;
        const piNo = issue.im_master_pi_no || '-';
        const woNo = issue.im_master_order_no || '-';

        itemsList.forEach((it: any) => {
          const itProdId = Number(it.product_id || it.item_id);
          const itProdName = it.product_name || it.item_name;
          if (itProdId === prodId || (itProdName && itProdName.toLowerCase() === prodName.toLowerCase())) {
            const qty = Number(it.qty || it.issued_qty || it.pcs || 0);
            totalIssued += qty;
            matchedIssues.push({
              issue_id: issue.im_id,
              issue_to: issueTo,
              date: issueDate,
              pi_no: piNo,
              wo_no: woNo,
              qty: qty,
            });
          }
        });
      });

      // Available quantity (use stock from products table or purchased - issued)
      const stockFromDb = Number(p.product_qty || 0);
      const availableQty = stockFromDb > 0 ? stockFromDb : Math.max(0, totalPurchased - totalIssued);
      const effectivePurchased = totalPurchased > 0 ? totalPurchased : availableQty + totalIssued;

      // Weighted average rate
      const avgRate = totalPurchased > 0 ? totalPurchaseCost / totalPurchased : Number(p.product_price || 0);
      const totalAmount = availableQty * avgRate;

      // Matched Work Orders
      const relevantPiNos = new Set(matchedIssues.map((r) => String(r.pi_no).toLowerCase()).filter((x) => x && x !== '-'));
      const matchedWOs = workOrders.filter((wo) => {
        const piOrder = String(wo.pi_order_no || '').toLowerCase();
        return relevantPiNos.has(piOrder);
      });

      return {
        key: `ply_mdf_${prodId}`,
        product_id: prodId,
        product_name: prodName,
        product_code: prodCode,
        category_name: categoryName,
        brand_name: brandName,
        unit_name: unitName,
        total_purchased_qty: parseFloat(effectivePurchased.toFixed(2)),
        total_issued_qty: parseFloat(totalIssued.toFixed(2)),
        available_qty: parseFloat(availableQty.toFixed(2)),
        avg_rate: parseFloat(avgRate.toFixed(2)),
        total_amount: parseFloat(totalAmount.toFixed(2)),
        purchase_bills: matchedBills,
        issued_records: matchedIssues,
        work_orders: matchedWOs,
      };
    });
  }, [products, poBills, issues, workOrders]);

  // Filtered rows
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return inventoryData;
    const q = searchQuery.toLowerCase();
    return inventoryData.filter(
      (r) =>
        r.product_name.toLowerCase().includes(q) ||
        r.category_name.toLowerCase().includes(q) ||
        r.brand_name.toLowerCase().includes(q) ||
        r.product_code.toLowerCase().includes(q)
    );
  }, [inventoryData, searchQuery]);

  // Summary Metrics
  const totalStockQty = useMemo(() => {
    return inventoryData.reduce((sum, r) => sum + r.available_qty, 0);
  }, [inventoryData]);

  const totalInventoryValue = useMemo(() => {
    return inventoryData.reduce((sum, r) => sum + r.total_amount, 0);
  }, [inventoryData]);

  const totalIssuedAll = useMemo(() => {
    return inventoryData.reduce((sum, r) => sum + r.total_issued_qty, 0);
  }, [inventoryData]);

  // Export Excel
  const handleExportExcel = () => {
    const data = filteredRows.map((r, idx) => ({
      'Sr No.': idx + 1,
      'Product / Item Name': r.product_name,
      'Product Code': r.product_code,
      'Category': r.category_name,
      'Brand': r.brand_name,
      'Available Qty': `${r.available_qty} ${r.unit_name}`,
      'Purchased Qty': `${r.total_purchased_qty} ${r.unit_name}`,
      'Issued Qty': `${r.total_issued_qty} ${r.unit_name}`,
      'Avg Rate (₹ Excl. GST)': r.avg_rate,
      'Total Amount (₹ Excl. GST)': r.total_amount,
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ply and MDF Inventory');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Ply_and_MDF_Inventory_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`
    );
  };

  const handleOpenViewModal = (record: PlyMdfItem) => {
    setSelectedRow(record);
    setIsModalOpen(true);
  };

  const columns = [
    {
      title: 'Sr.',
      key: 'sr',
      width: 60,
      align: 'center' as const,
      render: (_: any, __: any, idx: number) => idx + 1,
    },
    {
      title: 'Item / Product Name',
      dataIndex: 'product_name',
      key: 'product_name',
      render: (name: string, record: PlyMdfItem) => (
        <div>
          <div style={{ fontWeight: 700, color: '#1a1a1a', fontSize: 14 }}>{name}</div>
          {record.product_code && record.product_code !== '-' && (
            <div style={{ fontSize: 11, color: '#888' }}>Code: {record.product_code}</div>
          )}
        </div>
      ),
      sorter: (a: PlyMdfItem, b: PlyMdfItem) => a.product_name.localeCompare(b.product_name),
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category_name',
      width: 150,
      render: (cat: string) => (
        <Tag color="cyan" style={{ fontWeight: 600, borderRadius: 12, padding: '2px 10px' }}>
          {cat}
        </Tag>
      ),
      sorter: (a: PlyMdfItem, b: PlyMdfItem) => a.category_name.localeCompare(b.category_name),
    },
    {
      title: 'Brand',
      dataIndex: 'brand_name',
      key: 'brand_name',
      width: 140,
      render: (brand: string) => <span>{brand || '-'}</span>,
      sorter: (a: PlyMdfItem, b: PlyMdfItem) => a.brand_name.localeCompare(b.brand_name),
    },
    {
      title: 'Available Stock',
      dataIndex: 'available_qty',
      key: 'available_qty',
      width: 160,
      render: (qty: number, record: PlyMdfItem) => (
        <div>
          <span style={{ fontSize: 15, fontWeight: 800, color: qty > 0 ? '#52c41a' : '#8c8c8c' }}>
            {qty} {record.unit_name}
          </span>
          <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
            Total In: {record.total_purchased_qty} | Issued: {record.total_issued_qty}
          </div>
        </div>
      ),
      sorter: (a: PlyMdfItem, b: PlyMdfItem) => a.available_qty - b.available_qty,
    },
    {
      title: 'Avg Rate (₹/Unit)',
      dataIndex: 'avg_rate',
      key: 'avg_rate',
      width: 150,
      render: (rate: number, record: PlyMdfItem) => (
        <span style={{ fontWeight: 600, color: '#1677ff', fontSize: 14 }}>
          ₹ {rate.toFixed(2)}
        </span>
      ),
      sorter: (a: PlyMdfItem, b: PlyMdfItem) => a.avg_rate - b.avg_rate,
    },
    {
      title: 'Total Amount (Excl. GST)',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 190,
      render: (amt: number) => (
        <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
          ₹ {amt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
      sorter: (a: PlyMdfItem, b: PlyMdfItem) => a.total_amount - b.total_amount,
    },
    {
      title: 'Action',
      key: 'action',
      width: 110,
      align: 'center' as const,
      render: (_: any, record: PlyMdfItem) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleOpenViewModal(record)}
          style={{ borderRadius: 6, fontWeight: 600 }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px 20px', minHeight: '90vh', backgroundColor: '#f8fafc' }}>
      {/* ── Breadcrumb & Title ── */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/inventory-managements">Inventory</Breadcrumb.Item>
        <Breadcrumb.Item>Ply & MDF Inventory</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, color: '#0f172a' }}>
            🪵 Ply & MDF Inventory Tracking
          </h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
            Plywood sheets, MDF panels, blockboards live stock, weighted average purchase rate (excl. GST), member holding, and production consumption.
          </p>
        </div>

        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchAllData}
            loading={loading}
            style={{ borderRadius: 6, fontWeight: 600 }}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            style={{ borderRadius: 6, fontWeight: 600, backgroundColor: '#107c41', borderColor: '#107c41' }}
          >
            Export Excel
          </Button>
        </Space>
      </div>

      {/* ── Metrics Cards Banner ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>AVAILABLE SHEETS / STOCK</span>}
              value={totalStockQty}
              precision={0}
              valueStyle={{ color: '#52c41a', fontWeight: 800 }}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>TOTAL VALUE (EXCL. GST)</span>}
              value={totalInventoryValue}
              precision={2}
              prefix="₹"
              valueStyle={{ color: '#1677ff', fontWeight: 800 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>TOTAL ISSUED TO PRODUCTION</span>}
              value={totalIssuedAll}
              precision={0}
              valueStyle={{ color: '#fa8c16', fontWeight: 800 }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>VARIETIES IN STOCK</span>}
              value={inventoryData.length}
              valueStyle={{ color: '#722ed1', fontWeight: 800 }}
              prefix={<ToolOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Search & Filter Console ── */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <Input
          size="large"
          placeholder="Search by Ply/MDF name, code, brand, or category..."
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
          style={{ maxWidth: 450, borderRadius: 8 }}
        />
      </Card>

      {/* ── Main Inventory Table ── */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            dataSource={filteredRows}
            columns={columns}
            rowKey="key"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total}`,
            }}
            bordered
            size="middle"
            locale={{ emptyText: 'No Ply & MDF products found' }}
          />
        )}
      </div>

      {/* ── Location & Tracking Modal ── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18, fontWeight: 800 }}>
              🪵 Ply & MDF Exact Location & Live Tracking
            </span>
          </div>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        width={900}
        footer={[
          <Button key="close" type="primary" onClick={() => setIsModalOpen(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedRow && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 12 }}>
            {/* Header Summary */}
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
              <Descriptions.Item label="Item Name">
                <strong>{selectedRow.product_name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                <Tag color="cyan">{selectedRow.category_name}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Brand">
                {selectedRow.brand_name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Store Available Qty">
                <span style={{ fontWeight: 800, color: '#52c41a', fontSize: 16 }}>
                  {selectedRow.available_qty} {selectedRow.unit_name}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="Avg Purchase Price">
                <strong style={{ color: '#1677ff' }}>₹ {selectedRow.avg_rate} / {selectedRow.unit_name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Total Stock Value">
                <strong>₹ {selectedRow.total_amount.toLocaleString('en-IN')}</strong> (Excl. GST)
              </Descriptions.Item>
            </Descriptions>

            {/* Exact Location Lifecycle Timeline */}
            <div style={{ background: '#f8fafc', padding: 20, borderRadius: 10, border: '1px solid #e2e8f0' }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#0f172a' }}>
                📍 Real-Time Location & Department Flow:
              </div>

              <Timeline
                items={[
                  // 1. Store Location (Unissued)
                  {
                    color: selectedRow.available_qty > 0 ? 'green' : 'gray',
                    dot: <InboxOutlined style={{ fontSize: 18 }} />,
                    children: (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <strong style={{ fontSize: 14 }}>1. In Store / Sheet Warehouse (Raw Stock)</strong>
                          <Tag color={selectedRow.available_qty > 0 ? 'green' : 'default'}>
                            {selectedRow.available_qty > 0 ? 'Available in Store' : 'Out of Stock'}
                          </Tag>
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                          Stock on hand: <strong>{selectedRow.available_qty} {selectedRow.unit_name}</strong> ready for production issuance.
                        </div>
                      </div>
                    ),
                  },
                  // 2. Issued to Member (Till Consume)
                  {
                    color: selectedRow.issued_records.length > 0 ? 'orange' : 'gray',
                    dot: <UserOutlined style={{ fontSize: 18 }} />,
                    children: (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <strong style={{ fontSize: 14 }}>2. Issued with Member / Carpenter / CNC Operator (Till Consume)</strong>
                          <Tag color={selectedRow.issued_records.length > 0 ? 'orange' : 'default'}>
                            {selectedRow.total_issued_qty > 0 ? `${selectedRow.total_issued_qty} ${selectedRow.unit_name} Issued` : 'None Issued'}
                          </Tag>
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                          Sheets currently held by technicians for sizing, cutting & routering.
                        </div>

                        {selectedRow.issued_records.length > 0 && (
                          <Table
                            style={{ marginTop: 10 }}
                            size="small"
                            bordered
                            pagination={false}
                            dataSource={selectedRow.issued_records}
                            rowKey={(r, i) => `${r.issue_id}_${i}`}
                            columns={[
                              {
                                title: 'Member / Employee Name',
                                dataIndex: 'issue_to',
                                key: 'issue_to',
                                render: (name) => <strong>{name}</strong>,
                              },
                              {
                                title: 'Issued Qty',
                                dataIndex: 'qty',
                                key: 'qty',
                                render: (qty) => <span style={{ color: '#fa8c16', fontWeight: 700 }}>{qty} {selectedRow.unit_name}</span>,
                              },
                              {
                                title: 'Ref PI / Work Order',
                                key: 'ref',
                                render: (_, r) => `PI #${r.pi_no} / WO #${r.wo_no}`,
                              },
                              {
                                title: 'Issue Date',
                                dataIndex: 'date',
                                key: 'date',
                                render: (d) => (d ? dayjs(d).format('DD-MMM-YYYY') : '-'),
                              },
                            ]}
                          />
                        )}
                      </div>
                    ),
                  },
                  // 3. Machinery & Production Stages (Consume & Handover)
                  {
                    color: selectedRow.work_orders.length > 0 ? 'blue' : 'gray',
                    dot: <ToolOutlined style={{ fontSize: 18 }} />,
                    children: (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <strong style={{ fontSize: 14 }}>3. Machinery / CNC Cutting & Department Handover</strong>
                          <Tag color={selectedRow.work_orders.length > 0 ? 'blue' : 'default'}>
                            {selectedRow.work_orders.length > 0 ? `${selectedRow.work_orders.length} Active WOs` : 'Idle'}
                          </Tag>
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                          After sheet cutting, components move to Assembly, Sanding, Finishing, and Packing stages.
                        </div>

                        {selectedRow.work_orders.length > 0 && (
                          <Table
                            style={{ marginTop: 10 }}
                            size="small"
                            bordered
                            pagination={false}
                            dataSource={selectedRow.work_orders}
                            rowKey="wo_id"
                            columns={[
                              {
                                title: 'WO Number',
                                dataIndex: 'wo_number',
                                key: 'wo_number',
                                render: (wo, r) => wo || `WO-${r.wo_id}`,
                              },
                              {
                                title: 'Product Name',
                                dataIndex: 'product_name',
                                key: 'product_name',
                              },
                              {
                                title: 'Assigned Employee',
                                key: 'emp',
                                render: (_, r) => `${r.employee_fname || ''} ${r.employee_lname || ''}`.trim() || 'Unassigned',
                              },
                              {
                                title: 'Stage / Status',
                                dataIndex: 'wo_status',
                                key: 'wo_status',
                                render: (status) => (
                                  <Tag color={status === 'completed' ? 'green' : 'processing'}>
                                    {status ? status.toUpperCase() : 'MACHINERY STAGE'}
                                  </Tag>
                                ),
                              },
                            ]}
                          />
                        )}
                      </div>
                    ),
                  },
                  // 4. Completed
                  {
                    color: 'green',
                    dot: <CheckCircleOutlined style={{ fontSize: 18 }} />,
                    children: (
                      <div style={{ fontSize: 13, color: '#64748b' }}>
                        <strong>4. Final Product Packed & Dispatched</strong>
                      </div>
                    ),
                  },
                ]}
              />
            </div>

            {/* Purchase Bills Batch History */}
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: '#334155' }}>
                📦 Purchase Bills & Weighted Price History (Excl. GST):
              </div>
              <Table
                size="small"
                bordered
                pagination={false}
                dataSource={selectedRow.purchase_bills}
                rowKey={(r, i) => `${r.bill_id}_${i}`}
                columns={[
                  {
                    title: 'Bill No.',
                    dataIndex: 'bill_no',
                    key: 'bill_no',
                    render: (no) => <strong>{no}</strong>,
                  },
                  {
                    title: 'Date',
                    dataIndex: 'date',
                    key: 'date',
                    render: (d) => (d ? dayjs(d).format('DD-MMM-YYYY') : '-'),
                  },
                  {
                    title: 'Purchased Qty',
                    dataIndex: 'qty',
                    key: 'qty',
                    render: (qty) => `${qty} ${selectedRow.unit_name}`,
                  },
                  {
                    title: 'Rate (₹ Excl. GST)',
                    dataIndex: 'rate',
                    key: 'rate',
                    render: (r) => `₹ ${Number(r).toFixed(2)}`,
                  },
                  {
                    title: 'Total (₹)',
                    dataIndex: 'amount',
                    key: 'amount',
                    render: (amt) => `₹ ${Number(amt).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                  },
                ]}
                locale={{ emptyText: 'No purchase bill recorded' }}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PlyAndMdfInventory;
